import { useEffect, useState } from "react";
import { Plane, BedDouble, Car, Ticket, Plus, X, ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";
import { CAT_LABELS, fmtUSD } from "../../pages/Budget";

const EXPENSE_CATEGORIES = ["food", "transport", "lodging", "activities", "shopping", "misc"];
const ALL_CATEGORIES = ["flights", "lodging", "car_rental", "activities", "food", "transport", "shopping", "misc"];
const RES_ICON = { flight: Plane, hotel: BedDouble, car: Car, event: Ticket };

function resLabel(r) {
  const d = r.detail || {};
  if (r.type === "flight") return [d.airline, d.flight_number, d.departure_airport && d.arrival_airport ? `${d.departure_airport} → ${d.arrival_airport}` : null].filter(Boolean).join(" ") || "Flight";
  if (r.type === "hotel") return d.hotel_name || "Hotel";
  if (r.type === "car") return d.vendor ? `${d.vendor} car rental` : "Car rental";
  if (r.type === "event") return d.event_name || "Event";
  return "Reservation";
}

function CostCell({ tripId, res, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/trips/${tripId}/reservations/${res.id}/cost`, { est_cost: val === "" ? null : Number(val) });
      setEditing(false);
      onChanged();
    } catch { toast.error("Could not save cost"); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className="font-mono text-xs text-sand-500">$</span>
        <input type="number" min="0" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && save()}
          className="w-24 border border-sand-300 px-2 py-1 text-xs font-mono focus:outline-none focus:border-ink"
          data-testid={`res-cost-input-${res.id}`} />
        <button onClick={save} disabled={busy} className="font-mono text-[10px] uppercase text-ember hover:underline disabled:opacity-40" data-testid={`res-cost-save-${res.id}`}>Save</button>
        <button onClick={() => setEditing(false)} className="font-mono text-[10px] uppercase text-sand-400 hover:text-ink">Cancel</button>
      </span>
    );
  }
  return (
    <button onClick={() => { setVal(res.est_cost != null ? String(res.est_cost) : ""); setEditing(true); }}
      className="font-mono text-sm hover:text-ember group inline-flex items-center gap-1.5"
      data-testid={`res-cost-edit-${res.id}`}>
      {res.est_cost != null ? fmtUSD(res.est_cost, 2) : <span className="text-sand-400">add cost</span>}
      {res.cost_source === "offer" && <span className="font-mono text-[9px] uppercase tracking-wide text-sand-400">offer</span>}
    </button>
  );
}

function ActualTotal({ tripId, info, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const [busy, setBusy] = useState(false);
  const patch = async (value) => {
    setBusy(true);
    try {
      await api.patch(`/trips/${tripId}/budget`, { actual_total: value });
      setEditing(false);
      onChanged();
    } catch { toast.error("Could not save actual total"); } finally { setBusy(false); }
  };
  if (editing) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="font-mono text-sm text-sand-500">$</span>
        <input type="number" min="0" autoFocus value={val} onChange={(e) => setVal(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && val !== "" && patch(Number(val))}
          className="w-28 border border-sand-300 px-2 py-1.5 text-sm font-mono focus:outline-none focus:border-ink"
          data-testid="actual-total-input" />
        <button onClick={() => patch(Number(val))} disabled={busy || val === ""}
          className="font-mono text-[10px] uppercase text-ember hover:underline disabled:opacity-40" data-testid="actual-total-save">Save</button>
        <button onClick={() => setEditing(false)} className="font-mono text-[10px] uppercase text-sand-400 hover:text-ink">Cancel</button>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2">
      <button onClick={() => { setVal(info.actual_total != null ? String(info.actual_total) : ""); setEditing(true); }}
        className="font-display font-medium text-2xl tracking-tight hover:text-ember"
        data-testid="actual-total-edit">
        {info.actual_total != null ? fmtUSD(info.actual_total) : <span className="text-sand-300">—</span>}
      </button>
      {info.actual_total != null && (
        <button onClick={() => patch(null)} disabled={busy} className="text-sand-400 hover:text-error" title="Clear actual total" data-testid="actual-total-clear">
          <X size={13} />
        </button>
      )}
    </div>
  );
}

function CategoryActuals({ tripId, info, onChanged }) {
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const cur = info.actual_by_type || {};
    setVals(Object.fromEntries(ALL_CATEGORIES.map((c) => [c, cur[c] != null ? String(cur[c]) : ""])));
  }, [info]);

  const save = async () => {
    setBusy(true);
    try {
      const payload = Object.fromEntries(ALL_CATEGORIES.map((c) => [c, vals[c] === "" ? null : Number(vals[c])]));
      await api.patch(`/trips/${tripId}/budget`, { actual_by_type: payload });
      toast.success("Category actuals saved");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save actuals");
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border border-sand-200 p-4" data-testid="category-actuals">
      <p className="text-[11px] text-sand-500 mb-3">Optional — enter what you actually spent per category. Blank = use estimate.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {ALL_CATEGORIES.map((c) => (
          <div key={c}>
            <label className="label-tag block mb-1">{CAT_LABELS[c]}</label>
            <input type="number" min="0" value={vals[c] ?? ""} placeholder={info.est_by_cat?.[c] != null ? `est ${Math.round(info.est_by_cat[c])}` : "—"}
              onChange={(e) => setVals((p) => ({ ...p, [c]: e.target.value }))}
              className="w-full border border-sand-300 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-ink"
              data-testid={`cat-actual-${c}`} />
          </div>
        ))}
      </div>
      <div className="flex justify-end mt-3">
        <button onClick={save} disabled={busy} className="btn-swiss btn-primary text-xs disabled:opacity-50" data-testid="cat-actuals-save">
          {busy ? <Loader2 size={12} className="animate-spin mr-1.5" /> : null} Save actuals
        </button>
      </div>
    </div>
  );
}

function ExpenseForm({ tripId, onAdded }) {
  const [f, setF] = useState({ category: "food", label: "", amount: "", date: "" });
  const [busy, setBusy] = useState(false);
  const add = async () => {
    if (!f.label.trim() || f.amount === "") { toast.error("Expense needs a label and amount"); return; }
    setBusy(true);
    try {
      await api.post(`/trips/${tripId}/expenses`, {
        category: f.category, label: f.label.trim(), amount: Number(f.amount), date: f.date || undefined,
      });
      setF({ category: "food", label: "", amount: "", date: "" });
      onAdded();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add expense");
    } finally { setBusy(false); }
  };
  const input = "border border-sand-300 px-2 py-1.5 text-xs font-mono focus:outline-none focus:border-ink bg-surface";
  return (
    <div className="flex flex-wrap items-center gap-2 mt-2">
      <select value={f.category} onChange={(e) => setF((p) => ({ ...p, category: e.target.value }))} className={input} data-testid="expense-category">
        {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{CAT_LABELS[c]}</option>)}
      </select>
      <input value={f.label} onChange={(e) => setF((p) => ({ ...p, label: e.target.value }))} placeholder="Dinner at Marmalade"
        className={`${input} flex-1 min-w-[140px]`} data-testid="expense-label" />
      <input type="number" min="0" value={f.amount} onChange={(e) => setF((p) => ({ ...p, amount: e.target.value }))} placeholder="$"
        className={`${input} w-20`} data-testid="expense-amount" />
      <input type="date" value={f.date} onChange={(e) => setF((p) => ({ ...p, date: e.target.value }))} className={input} data-testid="expense-date" />
      <button onClick={add} disabled={busy} className="btn-swiss btn-primary text-xs disabled:opacity-50" data-testid="expense-add-btn">
        <Plus size={12} className="mr-1" /> Add
      </button>
    </div>
  );
}

export default function TripBudget({ tripId }) {
  const [info, setInfo] = useState(null);
  const [showCats, setShowCats] = useState(false);

  const load = () => {
    api.get(`/trips/${tripId}/budget`).then(({ data }) => setInfo(data)).catch(() => {});
  };
  useEffect(load, [tripId]);

  if (!info) return null;

  const deleteExpense = async (eid) => {
    try {
      await api.delete(`/trips/${tripId}/expenses/${eid}`);
      load();
    } catch { toast.error("Could not delete expense"); }
  };

  const activeRes = info.reservations.filter((r) => r.booking_status !== "cancelled");

  return (
    <section className="mt-12 bg-surface border-2 border-ink" data-testid="trip-budget">
      <div className="p-6 border-b-2 border-ink flex flex-wrap items-end justify-between gap-6">
        <p className="label-tag">Budget</p>
        <div className="flex flex-wrap gap-8">
          <div>
            <p className="label-tag mb-1">Estimated</p>
            <p className="font-display font-medium text-2xl tracking-tight" data-testid="trip-budget-estimated">{fmtUSD(info.est_total)}</p>
          </div>
          <div>
            <p className="label-tag mb-1">Actual total</p>
            <ActualTotal tripId={tripId} info={info} onChanged={load} />
          </div>
          <div>
            <p className="label-tag mb-1">Effective</p>
            <p className="font-display font-medium text-2xl tracking-tight text-ember" data-testid="trip-budget-effective">{fmtUSD(info.eff_total)}</p>
          </div>
        </div>
      </div>

      <div className="p-6 grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <p className="label-tag mb-3">Reservation costs</p>
          {activeRes.length === 0 ? (
            <p className="text-sm text-sand-500">No active reservations.</p>
          ) : (
            <div className="space-y-2">
              {activeRes.map((r) => {
                const Icon = RES_ICON[r.type] || Ticket;
                return (
                  <div key={r.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-sand-100" data-testid={`budget-res-${r.id}`}>
                    <span className="flex items-center gap-2 text-sm min-w-0">
                      <Icon size={13} className="text-sand-400 shrink-0" />
                      <span className="truncate">{resLabel(r)}</span>
                    </span>
                    <CostCell tripId={tripId} res={r} onChanged={load} />
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <p className="label-tag mb-3">Expenses</p>
          {info.expenses.length === 0 ? (
            <p className="text-sm text-sand-500">No expense line items yet — meals, taxis, activities…</p>
          ) : (
            <div className="space-y-2">
              {info.expenses.map((e) => (
                <div key={e.id} className="flex items-center justify-between gap-3 py-1.5 border-b border-sand-100 group" data-testid={`expense-row-${e.id}`}>
                  <span className="text-sm min-w-0 truncate">
                    {e.label}
                    <span className="font-mono text-[10px] uppercase tracking-wide text-sand-400 ml-2">{CAT_LABELS[e.category] || e.category}{e.date ? ` · ${e.date}` : ""}</span>
                  </span>
                  <span className="flex items-center gap-2 shrink-0">
                    <span className="font-mono text-sm">{fmtUSD(e.amount, 2)}</span>
                    <button onClick={() => deleteExpense(e.id)} className="opacity-0 group-hover:opacity-100 text-sand-400 hover:text-error transition-opacity" data-testid={`expense-delete-${e.id}`}>
                      <X size={12} />
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
          <ExpenseForm tripId={tripId} onAdded={load} />
        </div>
      </div>

      <div className="px-6 pb-6">
        <button onClick={() => setShowCats(!showCats)}
          className="font-mono text-[11px] uppercase tracking-wide text-sand-500 hover:text-ink flex items-center gap-1.5"
          data-testid="toggle-category-actuals">
          {showCats ? <ChevronUp size={12} /> : <ChevronDown size={12} />} Actuals by category
        </button>
        {showCats && <CategoryActuals tripId={tripId} info={info} onChanged={load} />}
      </div>
    </section>
  );
}
